import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  beginCrmOperation,
  changeCrmScope,
  createCrmGuard,
  endCrmOperation,
  type CrmGuard,
  type CrmViewModel,
} from './crmWorkspaceState';
export interface CrmCommands {
  preview: () => void;
  confirm: () => void;
  back: () => void;
}
interface WorkflowContext {
  commands: { current: CrmCommands | null };
  guard: CrmGuard;
  model: CrmViewModel | null;
  publish: (value: CrmViewModel | null) => void;
  refresh: () => void;
}
const Context = createContext<WorkflowContext | null>(null);
export function CrmWorkflowProvider({ scope, children }: { scope: string; children: ReactNode }) {
  const guard = useRef(createCrmGuard(scope));
  changeCrmScope(guard.current, scope);
  const commands = useRef<CrmCommands | null>(null);
  const [model, setModel] = useState<CrmViewModel | null>(null);
  const [, setRevision] = useState(0);
  return (
    <Context.Provider
      value={{
        commands,
        guard: guard.current,
        model,
        publish: setModel,
        refresh: () => setRevision((n) => n + 1),
      }}
    >
      {children}
    </Context.Provider>
  );
}
export const useCrmWorkflow = () => useContext(Context);
export function useCrmAdapter(model: CrmViewModel, commands?: CrmCommands) {
  const workspace = useCrmWorkflow();
  const local = useRef(createCrmGuard('standalone', true));
  const guard = workspace?.guard ?? local.current;
  useLayoutEffect(() => {
    if (workspace) workspace.commands.current = commands ?? null;
  }, [workspace?.commands, commands]);
  const { purpose, phase, dirty, busy, unresolved, canPreview, canConfirm } = model;
  useLayoutEffect(() => {
    guard.dirty = dirty;
    // Cross-purpose pending records are checked separately by the workspace.
    workspace?.publish({ purpose, phase, dirty, busy, unresolved, canPreview, canConfirm });
  }, [purpose, phase, dirty, busy, unresolved, canPreview, canConfirm, guard]);
  return {
    embedded: !!workspace,
    begin: (intent: 'new' | 'lookup' | 'original-retry' = 'new') => {
      if (intent === 'new' && (guard.unresolved || !guard.newWritesAllowed)) return null;
      const token = beginCrmOperation(guard);
      workspace?.refresh();
      return token;
    },
    end: (token: NonNullable<ReturnType<typeof beginCrmOperation>>) => {
      const ended = endCrmOperation(guard, token);
      if (ended) workspace?.refresh();
      return ended;
    },
  };
}
